// Tickets tab — support-relationship intelligence (Phase 3).
// Renders the cached TicketAnalysis artifact (GET /tickets/analysis, nightly job)
// + the ticket table. ?mock=1 keeps the design mock for offline visual work.
// docs/design/2026-07-17-tickets-intelligence.md + design/README.md "Tickets".
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button } from "@mui/material";
import { useSnackbar } from "notistack";
import {
  getTicketAnalysis,
  refreshTicketAnalysis,
  type CustomerTickets,
  type Ticket,
  type TicketAnalysis,
  type FrictionLevel,
} from "../../lib/brain-api";
import { brainTokens } from "../../design/brainTheme";
import { SectionCard, MonoLabel, StatusPill, statusTone, ExplainButton, AgentText, Icon } from "../../mui";
import { useAgent } from "../../agent/AgentProvider";
import { Referable } from "../../agent/Referable";
import { mockTicketAnalysis, mockTickets } from "./mocks/ticketAnalysis.mock";

const c = brainTokens.color;

const FRICTION: Record<FrictionLevel, { word: string; wordColor: string; dot: string; segTint: string }> = {
  calm: { word: "Calm", wordColor: c.successText, dot: c.successDot, segTint: "#cfe9d8" },
  normal: { word: "Normal", wordColor: c.tealInk2, dot: c.teal, segTint: "#bfe3e0" },
  elevated: { word: "Elevated", wordColor: c.warnText, dot: c.warnDot, segTint: "#f4d9b3" },
  hot: { word: "Hot", wordColor: c.dangerText2, dot: c.dangerText2, segTint: "#f0d0cf" },
};
const FRICTION_ORDER: FrictionLevel[] = ["calm", "normal", "elevated", "hot"];

function trendArrow(t: "up" | "down" | "flat") {
  return t === "up" ? "↑" : t === "down" ? "↓" : "→";
}

/** Volume sparkline: teal line + dashed baseline + endpoint dot (design "Tickets"). */
function Sparkline({ months, baseline }: { months: { month: string; count: number }[]; baseline: number }) {
  const W = 220;
  const H = 40;
  const vals = months.map((m) => m.count);
  const max = Math.max(...vals, baseline, 1);
  const y = (v: number) => H - 4 - (v / max) * (H - 12);
  const stepX = vals.length > 1 ? W / (vals.length - 1) : W;
  const pts = vals.map((v, i) => `${(i * stepX).toFixed(0)},${y(v).toFixed(0)}`).join(" ");
  const last = vals.length - 1;
  return (
    <Box component="svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" sx={{ width: "100%", height: 36, display: "block", mb: "8px" }}>
      <line x1="0" y1={y(baseline)} x2={W} y2={y(baseline)} stroke="#bfe0de" strokeWidth="1" strokeDasharray="3 3" />
      <polyline points={pts} fill="none" stroke={c.tealInk2} strokeWidth="2" vectorEffect="non-scaling-stroke" />
      <circle cx={last * stepX} cy={y(vals[last] ?? 0)} r="3" fill={c.tealInk2} />
    </Box>
  );
}

export function CustomerTicketsTabMui({
  customerId,
  tickets,
  isAdmin,
}: {
  customerId: string;
  tickets: CustomerTickets | null;
  isAdmin: boolean;
}) {
  const [searchParams] = useSearchParams();
  const useMock = searchParams.get("mock") === "1";
  const { openAgent } = useAgent();
  const { enqueueSnackbar } = useSnackbar();
  const [themeId, setThemeId] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<TicketAnalysis | null>(useMock ? mockTicketAnalysis : null);
  const [loading, setLoading] = useState(!useMock);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (useMock) {
      setAnalysis(mockTicketAnalysis);
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    getTicketAnalysis(customerId)
      .then((a) => live && setAnalysis(a))
      .catch(() => live && setAnalysis(null))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [customerId, useMock]);

  // Live Jira tickets for the table; the mock rows only stand in under ?mock=1.
  const rows: Ticket[] = tickets?.tickets?.length ? tickets.tickets : useMock ? mockTickets : [];
  const selectedTheme = analysis?.themes.find((t) => t.id === themeId) ?? null;
  const filteredRows = useMemo(
    () => (selectedTheme ? rows.filter((r) => selectedTheme.ticketKeys.includes(r.key)) : rows),
    [rows, selectedTheme],
  );

  const refresh = async () => {
    setRefreshing(true);
    try {
      const started = await refreshTicketAnalysis(customerId);
      enqueueSnackbar(started ? "Analysis queued — check back shortly." : "Analysis job not configured yet.", {
        variant: started ? "success" : "warning",
      });
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Refresh failed.", { variant: "error" });
    } finally {
      setRefreshing(false);
    }
  };

  const draftAnswer = (r: Ticket) =>
    openAgent({
      kind: "draft-ticket",
      modelHint: "smart",
      ticket: { key: r.key, summary: r.summary },
      prompt: `Draft a customer-appropriate reply for ticket ${r.key} — "${r.summary}". Search similar resolved tickets fleet-wide, check this customer's install/version, cite every source with [ref:...], and state your confidence. Do not post anything — this is a draft for a human to send.`,
    });

  if (loading) return <Box sx={{ color: c.muted2, fontSize: 14, py: 4 }}>Loading analysis…</Box>;

  // No cached artifact yet — the design's empty pattern: AI panel says so + admin
  // refresh, while the live ticket table (from GET /tickets) still renders.
  if (!analysis) {
    const counts = tickets?.counts;
    return (
      <Box>
        <Box sx={{ background: c.tealTint2, border: `1px solid ${c.tealTintBorder}`, borderRadius: "8px", p: "15px 17px", mb: "16px", display: "flex", gap: "12px", alignItems: "flex-start", flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <MonoLabel color={c.teal}>AGENT · NIGHTLY SUMMARY</MonoLabel>
            <Box sx={{ fontSize: 14, color: "#1f3a3a", lineHeight: 1.6, mt: "5px" }}>
              No analysis yet — the nightly job hasn't produced an artifact for this customer. It runs after hours;
              trigger one now if you can't wait.
            </Box>
          </Box>
          {isAdmin && (
            <Button variant="contained" disabled={refreshing} onClick={() => void refresh()} startIcon={<Icon name="refresh" size={15} stroke={1.8} />}>
              Refresh analysis
            </Button>
          )}
        </Box>
        {counts && (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px", mb: "20px" }}>
            <PulseCard label="Open"><Box sx={{ fontSize: 26, fontWeight: 700 }}>{counts.open}</Box></PulseCard>
            <PulseCard label="In progress"><Box sx={{ fontSize: 26, fontWeight: 700 }}>{counts.inProgress}</Box></PulseCard>
            <PulseCard label="Done · 30d"><Box sx={{ fontSize: 26, fontWeight: 700 }}>{counts.done30d}</Box></PulseCard>
          </Box>
        )}
        <TicketTable rows={filteredRows} onDraft={draftAnswer} />
      </Box>
    );
  }

  const stamp = (() => {
    try {
      const d = new Date(analysis.generatedAt);
      return `Synthesised ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")} today`;
    } catch {
      return "Synthesised today";
    }
  })();
  const fr = FRICTION[analysis.friction.level];
  const frictionActive = FRICTION_ORDER.indexOf(analysis.friction.level);

  return (
    <Box>
      {/* nightly AI summary */}
      <Box sx={{ mb: "16px" }}>
        <AgentPanelSummary text={analysis.summary.text} stamp={stamp} />
      </Box>

      {/* pulse row */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", mb: "24px" }}>
        {/* volume */}
        <PulseCard label="Ticket volume · 12 mo" explain={<ExplainButton snapshot={{ label: "Ticket volume", value: `${analysis.volume.months.at(-1)?.count ?? 0} new this month` }} />}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "7px", mb: "8px" }}>
            <Box sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>{analysis.volume.months.at(-1)?.count ?? 0}</Box>
            <Box sx={{ fontSize: 12.5, color: c.faint }}>new this month</Box>
          </Box>
          <Sparkline months={analysis.volume.months} baseline={analysis.volume.baseline} />
          <Box sx={{ fontSize: 11.5, color: c.successText }}>
            {analysis.volume.anomaly ?? "Below their baseline"} · <Box component="span" sx={{ color: c.faint }}>avg {analysis.volume.baseline}/mo</Box>
          </Box>
        </PulseCard>

        {/* response & resolution */}
        <PulseCard label="Response & resolution" explain={<ExplainButton snapshot={{ label: "Response & resolution", value: `${analysis.responsiveness.firstResponseHours}h first response` }} />}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "11px" }}>
            <TrendRow label="First response" value={`${analysis.responsiveness.firstResponseHours}h`} pct={analysis.responsiveness.firstResponseTrendPct} />
            <TrendRow label="Resolution" value={`${analysis.responsiveness.resolutionDays}d`} pct={analysis.responsiveness.resolutionTrendPct} />
          </Box>
          <Box sx={{ fontSize: 11, color: c.faint, mt: "11px" }}>median · rolling 90 days</Box>
        </PulseCard>

        {/* waiting on */}
        <PulseCard label="Waiting on">
          <Box sx={{ display: "flex", gap: "16px", mb: "12px" }}>
            <WaitStat n={analysis.waitingOn.ourCourt} label="Our court" />
            <WaitStat n={analysis.waitingOn.withClient} label="With client" />
            <WaitStat n={analysis.waitingOn.staleBothSides} label="Stale (both)" />
          </Box>
          {analysis.waitingOn.withClientOver21d > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "7px", background: c.dangerBg, border: "1px solid #f3d3d3", borderRadius: "6px", padding: "6px 9px" }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: c.dangerText, flex: "none" }} />
              <Box sx={{ fontSize: 11.5, color: "#a5302f", fontWeight: 600 }}>{analysis.waitingOn.withClientOver21d} with client &gt; 21 days</Box>
            </Box>
          )}
        </PulseCard>

        {/* friction */}
        <PulseCard label="Friction" explain={<ExplainButton snapshot={{ label: "Friction", value: fr.word }} prompt={`Explain why support friction for this customer is ${fr.word}. Cite the tickets and reopens driving it.`} />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "9px" }}>
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", background: fr.dot }} />
            <Box sx={{ fontSize: 20, fontWeight: 700, color: fr.wordColor }}>{fr.word}</Box>
          </Box>
          <Box sx={{ fontSize: 12, color: c.muted, lineHeight: 1.5 }}>
            {analysis.friction.reasons[0] ? <AgentText text={analysis.friction.reasons[0].text} /> : "No friction signals."}
          </Box>
          <Box sx={{ display: "flex", gap: "4px", mt: "12px" }}>
            {FRICTION_ORDER.map((lvl, i) => (
              <Box key={lvl} sx={{ flex: 1, height: 5, borderRadius: "3px", background: i === frictionActive ? FRICTION[lvl].dot : FRICTION[lvl].segTint }} />
            ))}
          </Box>
        </PulseCard>
      </Box>

      {/* themes */}
      {analysis.themes.length > 0 && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "12px", flexWrap: "wrap" }}>
            <Box sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: c.faint }}>Themes</Box>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px", fontFamily: brainTokens.font.mono, fontSize: 9.5, letterSpacing: "0.1em", color: c.teal, background: "#e6f2f1", border: `1px solid ${c.explainBorder}`, borderRadius: "4px", padding: "2px 6px" }}>
              ✦ AI-CLUSTERED
            </Box>
            <Box sx={{ fontSize: 12, color: c.faint2 }}>select to filter the table</Box>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", mb: "22px" }}>
            {analysis.themes.map((t) => {
              const on = t.id === themeId;
              return (
                <Box
                  key={t.id}
                  onClick={() => setThemeId(on ? null : t.id)}
                  sx={{ background: on ? "#f2faf9" : "#fff", border: on ? `1.5px solid ${c.teal}` : `1px solid ${c.border}`, borderRadius: "8px", p: on ? "13.5px 15px" : "14px 15px", cursor: "pointer", "&:hover": { borderColor: on ? c.teal : "#c7d6d6" } }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px", mb: "9px" }}>
                    <Box sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }}>{t.name}</Box>
                    <Box sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1, flex: "none", color: t.trend === "up" ? c.warnText2 : c.faint }}>{trendArrow(t.trend)}</Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "8px" }}>
                    <Box sx={{ fontSize: 11.5, fontWeight: 700, color: c.tealInk, background: c.tealTint, borderRadius: "999px", padding: "2px 9px" }}>{t.openCount} open</Box>
                    <Box sx={{ fontSize: 11.5, color: c.faint2 }}>{t.since}</Box>
                  </Box>
                  <Box sx={{ fontSize: 12, color: c.muted2, lineHeight: 1.5 }}>{t.synopsis}</Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}

      {/* filter chip */}
      {selectedTheme && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "9px", mb: "12px" }}>
          <Box sx={{ fontSize: 12.5, color: c.muted }}>Filtered by theme:</Box>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "9px", fontSize: 12.5, fontWeight: 600, color: c.tealInk, background: c.tealTint, borderRadius: "999px", padding: "4px 8px 4px 13px" }}>
            {selectedTheme.name}
            <Box component="span" onClick={() => setThemeId(null)} sx={{ cursor: "pointer", color: c.tealInk, display: "inline-flex", width: 16, height: 16, borderRadius: "50%", background: "#cfe9e6", alignItems: "center", justifyContent: "center", fontSize: 10 }}>
              ✕
            </Box>
          </Box>
        </Box>
      )}

      <TicketTable rows={filteredRows} onDraft={draftAnswer} filtered={!!selectedTheme} />
    </Box>
  );
}

function TicketTable({ rows, onDraft, filtered }: { rows: Ticket[]; onDraft: (r: Ticket) => void; filtered?: boolean }) {
  return (
    <Box sx={{ background: "#fff", border: `1px solid ${c.border}`, borderRadius: "8px", overflow: "hidden" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "120px 1fr 120px 130px 70px 100px 130px", gap: "14px", p: "13px 20px", background: c.panelBg2, borderBottom: `1px solid ${c.borderLight}`, fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: c.muted2 }}>
        <span>Key</span><span>Summary</span><span>Status</span><span>Assignee</span><span>Priority</span><span>Updated</span><span />
      </Box>
      {rows.length === 0 ? (
        <Box sx={{ p: "24px 20px", fontSize: 13, color: c.muted2, textAlign: "center" }}>No tickets{filtered ? " in this theme" : ""}.</Box>
      ) : (
        rows.map((r) => (
          <Box key={r.key} sx={{ display: "grid", gridTemplateColumns: "120px 1fr 120px 130px 70px 100px 130px", gap: "14px", p: "13px 20px", borderBottom: `1px solid ${c.borderLight3}`, alignItems: "center", fontSize: 13, "&:hover": { background: c.panelBg } }}>
            <Box component="a" href={r.url} target="_blank" rel="noopener noreferrer" sx={{ fontFamily: brainTokens.font.mono, fontSize: 12, color: c.tealInk2, textDecoration: "none", "&:hover": { color: c.tealHover } }}>
              {r.key}
            </Box>
            <Referable
              label={r.key}
              snapshot={{ kind: "ticket", key: r.key, summary: r.summary, status: r.status, assignee: r.assignee, priority: r.priority }}
            >
              <Box sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.summary}</Box>
            </Referable>
            <Box><StatusPill tone={statusTone(r.status)} label={r.status} /></Box>
            <Box sx={{ color: r.assignee ? c.ink3 : "#b0b6bc" }}>{r.assignee ?? "Unassigned"}</Box>
            <Box sx={{ color: c.muted }}>{r.priority ?? "—"}</Box>
            <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 11.5, color: c.muted2 }}>{r.updated}</Box>
            <Box sx={{ textAlign: "right" }}>
              <Box component="button" onClick={() => onDraft(r)} sx={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: 11.5, fontWeight: 500, color: c.teal, background: c.explainBg, border: `1px solid ${c.explainBorder}`, borderRadius: "6px", padding: "5px 9px", cursor: "pointer", fontFamily: brainTokens.font.sans, "&:hover": { background: "#d9efec" } }}>
                ✦ Draft answer
              </Box>
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}

function AgentPanelSummary({ text, stamp }: { text: string; stamp: string }) {
  return (
    <Box sx={{ background: c.tealTint2, border: `1px solid ${c.tealTintBorder}`, borderRadius: "8px", p: "15px 17px", display: "flex", gap: "12px", alignItems: "flex-start" }}>
      <Box sx={{ width: 26, height: 26, borderRadius: "7px", background: "#d9efec", color: c.teal, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
        <Box component="svg" width={14} height={14} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2l2.1 6.9L21 11l-6.9 2.1L12 20l-2.1-6.9L3 11l6.9-2.1z" />
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "9px", mb: "5px", flexWrap: "wrap" }}>
          <MonoLabel color={c.teal}>AGENT · NIGHTLY SUMMARY</MonoLabel>
          <Box component="span" sx={{ fontSize: 11, color: "#86b8b8" }}>{stamp}</Box>
        </Box>
        <Box sx={{ fontSize: 14, color: "#1f3a3a", lineHeight: 1.6 }}>
          <AgentText text={text} />
        </Box>
      </Box>
    </Box>
  );
}

function PulseCard({ label, explain, children }: { label: string; explain?: React.ReactNode; children: React.ReactNode }) {
  return (
    <SectionCard label={label} actions={explain} headerMb={10} padding={17}>
      {children}
    </SectionCard>
  );
}

function TrendRow({ label, value, pct }: { label: string; value: string; pct: number }) {
  // Down is good for both response & resolution time.
  const good = pct <= 0;
  const arrow = pct < 0 ? "↓" : pct > 0 ? "↑" : "→";
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
      <Box sx={{ fontSize: 12.5, color: c.muted }}>{label}</Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
        <Box sx={{ fontSize: 20, fontWeight: 700 }}>{value}</Box>
        <Box sx={{ fontSize: 12, fontWeight: 600, color: good ? c.successText : c.warnText2 }}>{arrow} {Math.abs(pct)}%</Box>
      </Box>
    </Box>
  );
}

function WaitStat({ n, label }: { n: number; label: string }) {
  return (
    <Box>
      <Box sx={{ fontSize: 20, fontWeight: 700 }}>{n}</Box>
      <Box sx={{ fontSize: 11, color: c.muted2 }}>{label}</Box>
    </Box>
  );
}
